import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PROCUREMENT_STATE_LABELS } from '@crm/shared'
import type { RequestItemProcurementState } from '@crm/shared'

/**
 * Серверные фильтры «дашборд РОЗ → очередь квот» (13.07). Живут в URL
 * (deep-link с кликов дашборда), НЕ пишутся в localStorage — обычный вид
 * очереди без этих параметров остаётся прежним.
 */
export interface DashboardQueueFilters {
  procState: RequestItemProcurementState | null
  onlyNew: boolean
  zeroPrice: boolean
  wentToInvoice: boolean
  /** Композиты эффективной шкалы (16.07): без предложения / в работе / без ответа. */
  noOffer: boolean
  inWork: boolean
  noAnswer: boolean
  /** Только «живые» заявки (клик «Просрочено» дашборда). */
  activeOnly: boolean
  buyerId: string | null
  /** Имя закупщика для подписи плашки (передаётся с дашборда, не обязателен). */
  buyerName: string | null
  requestType: 'TEKUCHKA' | 'PROJECT' | null
}

const PROC_STATES: RequestItemProcurementState[] = [
  'NEW',
  'REQUESTED',
  'WAITING',
  'NEEDS_INFO',
  'QUOTED',
  'NOT_FOUND',
  'NO_RESPONSE',
  'REFUSED',
]

export function useDashboardQueueLink() {
  const [searchParams, setSearchParams] = useSearchParams()

  const filters: DashboardQueueFilters = useMemo(() => {
    const rawState = searchParams.get('procState')
    const rawType = searchParams.get('requestType')
    return {
      procState: PROC_STATES.includes(rawState as RequestItemProcurementState)
        ? (rawState as RequestItemProcurementState)
        : null,
      onlyNew: searchParams.get('new') === '1',
      zeroPrice: searchParams.get('zeroPrice') === '1',
      wentToInvoice: searchParams.get('wentToInvoice') === '1',
      noOffer: searchParams.get('noOffer') === '1',
      inWork: searchParams.get('inWork') === '1',
      noAnswer: searchParams.get('noAnswer') === '1',
      activeOnly: searchParams.get('activeOnly') === '1',
      buyerId: searchParams.get('buyerId'),
      buyerName: searchParams.get('buyerName'),
      requestType: rawType === 'TEKUCHKA' || rawType === 'PROJECT' ? rawType : null,
    }
  }, [searchParams])

  const active =
    !!filters.procState ||
    filters.onlyNew ||
    filters.zeroPrice ||
    filters.wentToInvoice ||
    filters.noOffer ||
    filters.inWork ||
    filters.noAnswer ||
    filters.activeOnly ||
    !!filters.buyerId ||
    !!filters.requestType

  /** Точечная правка одного параметра (для чипов/селекта в тулбаре). */
  const setParam = useCallback(
    (key: string, value: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (value === null) next.delete(key)
          else next.set(key, value)
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  const reset = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        for (const k of [
          'procState',
          'new',
          'zeroPrice',
          'wentToInvoice',
          'noOffer',
          'inWork',
          'noAnswer',
          'activeOnly',
          'buyerId',
          'buyerName',
          'requestType',
        ])
          next.delete(k)
        return next
      },
      { replace: true },
    )
  }, [setSearchParams])

  /** Русская подпись активного фильтра для плашки над очередью. */
  const label = useMemo(() => {
    const parts: string[] = []
    if (filters.procState)
      parts.push(`статус закупщика: ${PROCUREMENT_STATE_LABELS[filters.procState]}`)
    if (filters.onlyNew) parts.push('только новые (не тронутые)')
    if (filters.zeroPrice) parts.push('отказ (цена 0)')
    if (filters.wentToInvoice) parts.push('ушло в счёт')
    if (filters.noOffer) parts.push('без предложения')
    if (filters.inWork) parts.push('в работе')
    if (filters.noAnswer) parts.push('без ответа')
    if (filters.activeOnly) parts.push('только живые заявки')
    if (filters.buyerId) parts.push(`закупщик: ${filters.buyerName ?? 'выбранный'}`)
    if (filters.requestType)
      parts.push(filters.requestType === 'PROJECT' ? 'только проекты' : 'только текучка')
    return parts.join(' · ')
  }, [filters])

  return { filters, active, label, setParam, reset }
}
